"use client";

import * as React from "react";
import { UserMenu, type UserMenuLink } from "@/components/user-menu";
import { signOut } from "@/app/actions/sign-out";

interface SessionData {
  email: string;
  tierLabel: string;
  links: UserMenuLink[];
}

/**
 * Account menu for the ATSA section's header. The ATSA pages render inside
 * client components (the interactive practice modules), so the session can't be
 * server-fetched the way the marketing <Nav> does it — instead we fetch
 * /api/nav-session on mount and render the same <UserMenu> dropdown (signed in)
 * or Sign in / Start now (anonymous). This is how a visitor reaches the rest of
 * modernatc.com (dashboard, study, account, etc.) from the practice area.
 */
export function AccountMenu() {
  const [state, setState] = React.useState<
    { status: "loading" } | { status: "anon"; appUrl: string } | { status: "auth"; data: SessionData }
  >({ status: "loading" });

  React.useEffect(() => {
    let active = true;
    fetch("/api/nav-session", { cache: "no-store" })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error("nav-session failed"))))
      .then((json: { session: SessionData | null; appUrl: string }) => {
        if (!active) return;
        if (json.session) setState({ status: "auth", data: json.session });
        else setState({ status: "anon", appUrl: json.appUrl || "https://app.modernatc.com" });
      })
      .catch(() => {
        if (active) setState({ status: "anon", appUrl: "https://app.modernatc.com" });
      });
    return () => {
      active = false;
    };
  }, []);

  if (state.status === "loading") {
    // Reserve space so the header doesn't shift when the menu resolves.
    return <span className="account-menu-skeleton" aria-hidden />;
  }

  if (state.status === "auth") {
    return (
      <UserMenu
        email={state.data.email}
        tierLabel={state.data.tierLabel}
        links={state.data.links}
        signOutSlot={
          <form action={signOut}>
            <button
              type="submit"
              className="w-full rounded-[var(--radius-sm)] px-3 py-2 text-left text-sm text-[var(--color-ink-dim)] hover:bg-[var(--color-panel-raised)] hover:text-[var(--color-ink)]"
              role="menuitem"
            >
              Sign out
            </button>
          </form>
        }
      />
    );
  }

  return (
    <div className="auth-cta">
      <a className="header-cta" href={`${state.appUrl}/login`}>
        Sign in
      </a>
      <a className="header-cta support" href={`${state.appUrl}/signup`}>
        Start now
      </a>
    </div>
  );
}
